
type NullableNumber = number | null | undefined;

const normalizeNumber = (value: NullableNumber): number | null => {
    if (value == null) {
        return null;
    }

    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
};

// Null-safe truncation. Overload keeps the existing contract for callers passing a
// guaranteed number (returns number), but also accepts null/undefined and returns
// null in that case — instead of the previous TypeError on `null.toFixed(...)`.
// Callers should propagate null downstream (e.g. chart data arrays render as gaps).
export function truncateNumber(value: number, decimalPlaces?: number): number;
export function truncateNumber(value: number | null | undefined, decimalPlaces?: number): number | null;
export function truncateNumber(value: number | null | undefined, decimalPlaces: number = 0): number | null {
    const numericValue = normalizeNumber(value);
    if (numericValue == null) {
        return null;
    }

    return Number(numericValue.toFixed(decimalPlaces));
}

/**
 * 824 -> 900
 * 1130 -> 1200
 * @param value
 * @return
 */
export const roundNextHundred = (value: NullableNumber) => {
    const numericValue = normalizeNumber(value);
    if (numericValue == null) {
        return 0.0
    }
    return truncateNumber(Math.ceil((numericValue / 100)) * 100);
}

const DEFAULT_POWER_AXIS_MAX = 6000;

export const calculatePowerAxisMax = (maxPower: number | null | undefined): number => {
    if (maxPower == null || maxPower <= 0) {
        return DEFAULT_POWER_AXIS_MAX;
    }
    return roundNextHundred(maxPower * 1.15);
}

/**
 * 1650 -> 2000
 * @param value
 * @return
 */
export const roundNextThousand = (value: NullableNumber) => {
    const numericValue = normalizeNumber(value);
    if (numericValue == null) {
        return 0.0
    }
    return truncateNumber(Math.ceil((numericValue / 1000)) * 1000);
}
/**
 *  input 23 -> Output = 30
 *  input 6 -> Output = 10
 *  input 1953 - > Output 1960
 * @param value
 * @return
 */
export const roundNextTen = (value: NullableNumber) => {
    const numericValue = normalizeNumber(value);
    if (numericValue == null) return 0.0;
    const round = Math.ceil(numericValue);
    return truncateNumber(Math.ceil(((round + 9) / 10) * 10));
}

export const formatNumberWithMaxTwoDecimals = (value: NullableNumber) => {
    const numericValue = normalizeNumber(value);
    if(numericValue == null) return 0;

    const valueStr = numericValue.toString();

    if (!valueStr.includes('.') || valueStr.split('.')[1].length <= 2) {
        return numericValue;
    }

    return Number.parseFloat(numericValue.toFixed(2));
};
